// Single source of truth: MQTT topic -> field -> display. Renaming a topic or
// a JSON key on the wire should only ever require editing a row here.
//
// `source` pins a row to one device: 'pi' for the denshi-camera client's own
// status. Rows without it are sensor readouts and are resolved from the
// controller ('denshi') first, then from the Pi bridge ('bridge', wire id
// denshi-pi) when the controller's own link is stale -- the bridge republishes
// the controller's records under the same message types and column names, so
// the same `path` reads both. lib/constants.js maps each to its hostname.
//
// `messageType` is the third `weco/<hostname>/<messageType>` path segment.
// None of these topics carry a fourth `sensor_id` segment or a `sensor_id`
// field in their payload, so the per-sensor key inside the raw message tree
// falls back to messageType itself -- see hooks/useDenshiState.js.
//
// `staleAfterMs` is roughly 2x each topic's normal publish interval (per the
// spec: GPS ~once/minute, status "occasionally", INA/ODrive at sensor rate),
// which is why it differs so much row to row -- staleness is per-field, not
// one global "last update".
export const FIELD_MAP = {
  // status
  connected: { messageType: 'status', path: (d) => d.connected, staleAfterMs: 120_000 },
  signalPercent: { messageType: 'status', path: (d) => d.signal_percentage, unit: '%', staleAfterMs: 120_000 },
  signalOperator: { messageType: 'status', path: (d) => d.signal_operator, staleAfterMs: 120_000 },
  machineState: { messageType: 'status', path: (d) => d.state, staleAfterMs: 120_000 },
  // resolved state of the transponder supply -- mqtt-to-bigquery's denshi.py
  // republishes it from the load-override work in weco-dashboard 465a2e6
  aisOn: { messageType: 'status', path: (d) => d.ais_on, staleAfterMs: 120_000 },

  // gps -- absent latitude/longitude means "running, no fix", not "no message"
  latitude: { messageType: 'gps', path: (d) => (d.latitude == null ? null : d.latitude), staleAfterMs: 120_000 },
  longitude: { messageType: 'gps', path: (d) => (d.longitude == null ? null : d.longitude), staleAfterMs: 120_000 },
  latDir: { messageType: 'gps', path: (d) => d.latitude_dir ?? null, staleAfterMs: 120_000 },
  lonDir: { messageType: 'gps', path: (d) => d.longitude_dir ?? null, staleAfterMs: 120_000 },
  // course is course-over-ground, not a compass heading -- near-meaningless on
  // a moored unit, kept clearly labelled as COG rather than HDG at render time
  courseOverGround: { messageType: 'gps', path: (d) => d.course, staleAfterMs: 120_000 },
  gpsSpeed: { messageType: 'gps', path: (d) => d.speed, staleAfterMs: 120_000 },

  // battery_dantech
  soc: { messageType: 'battery_dantech', path: (d) => d.soc, unit: '%', staleAfterMs: 60_000 },
  packVoltage: { messageType: 'battery_dantech', path: (d) => d.pack_voltage, unit: 'V', staleAfterMs: 60_000 },

  // environment -- ambient conditions inside the buoy. denshi.py already
  // converts Pa -> hPa and the centi-units to whole ones.
  pressure: { messageType: 'environment', path: (d) => d.pressure, unit: 'hPa', staleAfterMs: 60_000 },
  temperature: { messageType: 'environment', path: (d) => d.temperature, unit: '°C', staleAfterMs: 60_000 },
  humidity: { messageType: 'environment', path: (d) => d.humidity, unit: '%', staleAfterMs: 60_000 },

  // ina -- power is negative when feeding the bus, so production is -power
  inaPower: {
    messageType: 'ina',
    path: (d) => (d.power == null ? null : -d.power),
    unit: 'W',
    staleAfterMs: 15_000,
  },

  // odrive brake resistor (not the machine state -- see machineState above)
  brakeFlags: { messageType: 'odrive', path: (d) => d.brake_flags, staleAfterMs: 15_000 },
  brakeDuty: { messageType: 'odrive', path: (d) => d.brake_duty, staleAfterMs: 15_000 },

  // odrive drive state/motion. `axis_state` is what the drive reports back
  // (ODrive Heartbeat.Axis_State) -- deliberately not `control_state`, which
  // is what the axis was *commanded* to do and can differ.
  axisState: { messageType: 'odrive', path: (d) => d.axis_state, staleAfterMs: 15_000 },
  // wire carries rev/s; RPM is the useful readout
  motorRpm: {
    messageType: 'odrive',
    path: (d) => (d.velocity == null ? null : d.velocity * 60),
    unit: 'RPM',
    staleAfterMs: 15_000,
  },
  rackPosition: { messageType: 'odrive', path: (d) => d.rack_position, unit: 'm', staleAfterMs: 15_000 },

  // denshi-camera status -- the Pi alongside the controller, publishing its
  // own liveness every 60s plus a retained LWT of connected:false. It carries
  // no sensor data at all (no GPS, battery, INA or ODrive), so it can stand
  // in for the controller's *liveness* when that goes quiet, and for nothing
  // else. 2.5x the publish interval: a single missed minute shouldn't flip it.
  piConnected: { source: 'pi', messageType: 'status', path: (d) => d.connected, staleAfterMs: 150_000 },
  // The Pi's own capture state (STANDBY/ACTIVE), not the machine state.
  piState: { source: 'pi', messageType: 'status', path: (d) => d.state, staleAfterMs: 150_000 },
  piCpuTemp: { source: 'pi', messageType: 'status', path: (d) => d.cpu_temperature, unit: '°C', staleAfterMs: 150_000 },
  piDiskFree: { source: 'pi', messageType: 'status', path: (d) => d.disk_usage_free, unit: 'B', staleAfterMs: 150_000 },
};

// Fields buffered as a rolling time series for the trend plots. Everything
// else keeps only its latest value.
export const PLOTTED_FIELDS = ['rackPosition', 'inaPower'];

// Fields additionally averaged over a rolling hour, in coarse per-second
// buckets rather than raw samples -- see hooks/useDenshiState.js.
export const HOURLY_FIELDS = ['inaPower'];
